<template>
  <v-card :loading="loading">
    <message :messages="messages"></message>
    <v-card-text>
      <v-layout row wrap>
        <v-flex xs12 sm4>
          <v-subheader class="queryLabel">{{ labels.clmNo }}</v-subheader>
          <span class="queryValue">{{ form.clmNo }}</span>
        </v-flex>
        <v-flex xs12 sm4>
          <v-subheader class="queryLabel">{{ labels.verCnt }}</v-subheader>
          <span class="queryValue">{{ form.verCnt }}</span>
        </v-flex>
        <v-flex xs12 class="ma-0 pa-0" />
        <v-flex xs12 sm4>
          <v-subheader class="queryLabel">{{ labels.clmEvent }}</v-subheader>
          <span class="queryValue" v-if="form.clmEvent">{{ form.clmEvent }}</span>
          <span class="queryValue" v-else>-</span>
        </v-flex>
        <v-flex xs12 sm4>
          <v-subheader class="queryLabel">{{ labels.clmtNo }} / {{ labels.clmItem }}</v-subheader>
          <span class="queryValue">{{ form.clmtNo }} / {{ form.clmItem }}</span>
        </v-flex>
        <v-flex xs12 sm4>
          <v-subheader class="queryLabel">{{ labels.clmResCat }}</v-subheader>
          <span class="queryValue">{{ form.clmResCat }} - {{ form.clmResCatDesc }}</span>
        </v-flex>
        <v-flex xs12 sm4>
          <v-subheader class="queryLabel">{{ labels.riType }} / {{ labels.xol }}</v-subheader>
          <span class="queryValue">{{ form.riType }} / {{ form.xol }}</span>
        </v-flex>
        <v-flex xs12 sm4>
          <v-subheader class="queryLabel">{{ labels.lvlNo }}</v-subheader>
          <span class="queryValue">{{ form.lvlNo }}</span>
        </v-flex>
        <v-flex xs12 class="ma-0 pa-0" />
        <v-flex xs12 sm4 v-if="form.tranType">
          <v-subheader class="queryLabel">{{ labels.tranType }} / {{ labels.docNo }}</v-subheader>
          <span class="queryValue">{{ form.tranType }} - {{ tranType(form.tranType) }} / {{ form.docNo }}</span>
        </v-flex>
        <v-flex xs12 class="ma-0 pa-0" />
        <v-flex xs12 sm4 v-if="form.tranRel">
          <v-subheader class="queryLabel">{{ labels.tranDate }}</v-subheader>
          <span class="queryValue">{{ formatDisplayDate(new Date(form.tranDate)) }}</span>
        </v-flex>
        <v-flex xs12 class="ma-0 pa-0" />
        <v-flex xs12 sm4 v-if="mode === 'review' && !form.tranRel">
          <v-text-field
            v-model="form.rcvyAmt"
            :label="labels.rcvyAmt"
            data-vv-name="rcvyAmt"
            :data-vv-as="labels.rcvyAmt"
            :error-messages="errors.collect('rcvyAmt')"
            v-num-mask="{ negative: true, intScale: 14, decScale: 2 }"
            @change="rcvyAmtChanged"
          />
        </v-flex>
        <v-flex xs12 sm4 v-else>
          <v-subheader class="queryLabel">{{ labels.rcvyAmt }}</v-subheader>
          <span class="queryValue">{{ formatDisplayNumber(form.rcvyAmt) }}</span>
        </v-flex>
        <v-flex xs12 sm4>
          <v-subheader class="queryLabel">{{ labels.rcvyAmtLoc }}</v-subheader>
          <span class="queryValue">{{ formatDisplayNumber(form.rcvyAmtLoc) }}</span>
        </v-flex>
        <v-flex xs12 sm4>
          <v-subheader class="queryLabel">{{ labels.exchRate }}</v-subheader>
          <span class="queryValue">{{ formatDisplayNumber(form.exchRate, 5) }}</span>
        </v-flex>
        <v-flex xs12 class="ma-0 pa-0" />
        <v-flex xs12 sm4 v-if="form.tranRel">
          <v-subheader class="queryLabel">{{ labels.relDate }}</v-subheader>
          <span class="queryValue">{{ formatDisplayDate(form.relDate) }}</span>
        </v-flex>
        <v-flex xs12 sm4 v-if="form.tranRel">
          <v-subheader class="queryLabel">{{ labels.relUsr }}</v-subheader>
          <span class="queryValue">{{ form.relUsr }}</span>
        </v-flex>
      </v-layout>
    </v-card-text>
    <v-card-actions>
      <v-btn color="secondary" @click.prevent="doExit" :loading="loading">
        <span>{{ labels.return }}</span>
        <v-icon right>mdi-keyboard-backspace</v-icon>
      </v-btn>
      <v-btn
        color="primary"
        @click.prevent="
          form.action = 'save'
          doSave()
        "
        :loading="loading"
        v-if="mode === 'review' && !form.tranRel"
      >
        {{ labels.save }}
        <v-icon right>mdi-content-save</v-icon>
      </v-btn>
      <v-btn
        color="primary"
        @click.prevent="
          form.action = 'release'
          doSave()
        "
        :loading="loading"
        v-if="mode === 'review' && !form.tranRel"
      >
        {{ labels.release }}
        <v-icon right>mdi-check-circle-outline</v-icon>
      </v-btn>
    </v-card-actions>
    <v-dialog v-model="saving" persistent width="300">
      <v-card color="primary" dark>
        <v-card-text>
          Updating XOL Worksheet. Please wait.
          <v-progress-linear indeterminate color="white" class="mb-0"></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import MessageMixin from '@/mixins/message'
import clone from 'clone'
import { expandURI, filterQuery } from '@/utils/ajax'
import { formatDisplayDate } from '@/utils/date'
import { formatDisplayNumber, removeFormatNumber } from '@/utils/number'
import { tranTypes } from '@/const/common'

export default {
  props: {
    mode: {
      type: String,
      default: ''
    },
    refresh: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      loading: false,
      saving: false,
      labels: {
        save: 'Save',
        cancel: 'Cancel',
        release: 'Release',
        return: 'Return',
        clmNo: 'Claim No.',
        verCnt: 'Versiont Count',
        clmEvent: 'Claim Event No.',
        clmtNo: 'Claimant',
        clmItem: 'Claim Item No.',
        clmResCat: 'Claim Reserve Category',
        riType: 'RI Type',
        xol: 'Company',
        lvlNo: 'Level No.',
        tranType: 'Tran Type',
        docNo: 'Doc No.',
        tranDate: 'Recovered Date',
        rcvyAmt: 'Recovered Amount',
        rcvyAmtLoc: 'Recovered Amount (Local)',
        exchRate: 'Exchange Rate',
        relDate: 'Release Date',
        relUsr: 'Release By'
      },
      form: {
        clmNo: undefined,
        verCnt: undefined,
        clmEvent: undefined,
        clmtNo: undefined,
        clmItem: undefined,
        clmResCat: undefined,
        clmResCatDesc: undefined,
        riType: undefined,
        xol: undefined,
        lvlNo: undefined,
        tranType: undefined,
        docNo: undefined,
        tranDate: undefined,
        tranRel: undefined,
        rcvyAmt: undefined,
        rcvyAmtLoc: undefined,
        exchRate: undefined,
        relDate: undefined,
        relUsrId: undefined,
        relUsr: undefined,
        action: undefined
      }
    }
  },
  watch: {
    refresh: function (value) {
      if (this.id && this.id.clmNo) {
        this.fetchData()
      }
    }
  },
  computed: {
    menu() {
      return this.$store.state.xolRecovery.menu
    },
    id() {
      return this.$store.state.xolRecovery.id
    }
  },
  methods: {
    rcvyAmtChanged() {
      let rcvyAmt = removeFormatNumber(this.form.rcvyAmt)
      this.form.rcvyAmtLoc = rcvyAmt * this.form.exchRate
    },
    async fetchData() {
      this.resetMessages()
      const apiUrl = this.menu.xolRecovery
      if (apiUrl) {
        try {
          this.loading = true
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, this.id)))
          if (result.data) {
            this.form = result.data
            this.form.rcvyAmt = formatDisplayNumber(this.form.rcvyAmt, 2)
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
        this.loading = false
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    doExit() {
      this.$emit('close')
    },
    async doSave() {
      const apiURL = this.menu.updateXOLRecovery
      if (apiURL) {
        this.saving = true
        try {
          const body = clone(this.form)
          body.rcvyAmt = removeFormatNumber(body.rcvyAmt)
          const result = await this.$http.post(apiURL, body)
          if (result.data) {
            if (this.form.action === 'release') {
              this.$toast.success('Transaction has been released')
              this.doExit()
            } else {
              this.$toast.success('XOL Worksheet is updated')
              this.doExit()
            }
          } else {
            this.$toast.error('Unexpected response')
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
        }
        this.saving = false
      } else {
        this.errorMessage('Error: Missing API URL')
      }
    },
    tranType(val) {
      return tranTypes[val] || '?'
    },
    formatDisplayDate(value) {
      return formatDisplayDate(value)
    },
    formatDisplayNumber(value, scale) {
      return formatDisplayNumber(value, scale)
    }
  },
  mixins: [MessageMixin]
}
</script>
